import { NgxIndexedDBService, ObjectStoreMeta } from 'ngx-indexed-db';
import { Define } from '@app/models/define';
import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ReportRecord } from '@app/models/report';
import { TimeStampRecord } from '@app/models/time-stamp-record';
import { User } from '@app/models/user';
import { Utility } from '@app/models/utility';
import { AuthService } from '@app/services/auth.service';
import { ReportService } from '@app/services/report.service';
import { firstValueFrom } from 'rxjs';
import { Firestore } from '@angular/fire/firestore';
import {
  collection,
  doc,
  DocumentData,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
  QueryDocumentSnapshot
} from 'firebase/firestore';

export interface DialogData {
  record: ReportRecord;
  stamp: Date;
}

@Component({
  selector: 'app-shift-stamp-dialog',
  templateUrl: './shift-stamp-dialog.component.html',
  styleUrls: ['./shift-stamp-dialog.component.css']
})
export class ShiftStampDialogComponent implements OnInit {
  user: User;
  currentDateTextShort: string;
  targetDate: Date;
  targetDateText: string;
  shiftTargetDate: Date;
  shiftDateTextShort: string;
  submitting: boolean;
  canSubmit: boolean;
  canInc: boolean;
  canDec: boolean;
  editable: boolean;
  timeStampDoc?: QueryDocumentSnapshot<DocumentData>;
  kind: number;
  kindTitle = ['出社', '退社', '外出', '戻り'];

  constructor(private firestore: Firestore,
    public auth: AuthService,
    private dialogRef: MatDialogRef<ShiftStampDialogComponent>,
    private reportService: ReportService,
    private dbService: NgxIndexedDBService,
    @Inject(MAT_DIALOG_DATA) public keySet: DialogData) {
      dialogRef.disableClose = true;
    }

  ngOnInit() {
    this.submitting = false;
    this.editable = false;
    this.canSubmit = false;
    this.canInc = false;
    this.canDec = false;
    this.init();
  }

  async init() {
    return new Promise<void>(async (resolve, reject) => {
      this.user = await this.auth.getUser();

      // TODO 実打刻のtimeStampに重複が無いことを前提としているので、重複が有っても大丈夫なようにtime-stampのuidで一意に求めるようにしたい。
      const timeStampCollection = collection(this.firestore, 'time-stamp', 'stamps', this.user.uid);
      const timeStampQuery = query(
        timeStampCollection,
        where('timeStamp', '==', Utility.convertDateToFullDateTime(this.keySet.stamp))
      );
      const timeStampSnapShot = await getDocs(timeStampQuery);
      if (timeStampSnapShot.docs[0] !== undefined && timeStampSnapShot.docs.length === 1) {
        this.timeStampDoc = timeStampSnapShot.docs[0];
        const stamp = Object.assign({}, this.timeStampDoc.data()) as TimeStampRecord;
        this.kind = stamp.kind % 10;
        if (stamp.target !== undefined) {
          this.targetDateText = stamp.target;
          this.targetDate = Utility.createDateFromText(this.targetDateText);
        } else {
          this.targetDate = Utility.createDateFromText(stamp.timeStamp);
          if (this.targetDate.getHours() < 5) {
            // [0~5)時は前日扱い
            this.targetDate.setDate(this.targetDate.getDate() - 1);
          }
          this.targetDateText = Utility.convertDateToFullDateTime(this.targetDate);
        }
        this.shiftTargetDate = new Date(this.targetDate);
        // 0～5時は前日扱い
        // if (Utility.createDateFromText(stamp.timeStamp).getHours() < 5) {
        //   this.targetDate.setDate(this.targetDate.getDate() - 1);
        // }
        const dateDiff = ['前', '　', '翌'];
        let dateDiffIndex = Utility.diffDay(this.shiftTargetDate, this.keySet.stamp) + 1;
        this.shiftDateTextShort = dateDiff[dateDiffIndex] + Utility.convertDateToMinite(this.shiftTargetDate);
        dateDiffIndex = Utility.diffDay(this.targetDate, this.keySet.stamp) + 1;
        this.currentDateTextShort = dateDiff[dateDiffIndex] + Utility.convertDateToMinite(this.keySet.stamp);
      }
      this.editable = true;

      this.canInc = Utility.diffDay(this.shiftTargetDate, this.keySet.stamp) !== -1;
      this.canDec = Utility.diffDay(this.shiftTargetDate, this.keySet.stamp) !== 1;

      resolve();
    }).catch(err => console.error(err));
  }

  onInc() {
    this.shiftTargetDate.setDate(this.shiftTargetDate.getDate() + 1);
    const dateDiff = ['前', '　', '翌'];
    const dateDiffIndex = Utility.diffDay(this.shiftTargetDate, this.keySet.stamp) + 1;
    this.shiftDateTextShort = dateDiff[dateDiffIndex] + Utility.convertDateToMinite(this.shiftTargetDate);
    if (this.targetDate.getDate() === this.shiftTargetDate.getDate()) {
      this.canSubmit = false;
    } else {
      this.canSubmit = true;
    }
    this.canInc = Utility.diffDay(this.shiftTargetDate, this.keySet.stamp) !== -1;
    this.canDec = Utility.diffDay(this.shiftTargetDate, this.keySet.stamp) !== 1;
  }

  onDec() {
    this.shiftTargetDate.setDate(this.shiftTargetDate.getDate() - 1);
    const dateDiff = ['前', '　', '翌'];
    const dateDiffIndex = Utility.diffDay(this.shiftTargetDate, this.keySet.stamp) + 1;
    this.shiftDateTextShort = dateDiff[dateDiffIndex] + Utility.convertDateToMinite(this.shiftTargetDate);
    if (this.targetDate.getDate() === this.shiftTargetDate.getDate()) {
      this.canSubmit = false;
    } else {
      this.canSubmit = true;
    }
    this.canInc = Utility.diffDay(this.shiftTargetDate, this.keySet.stamp) !== -1;
    this.canDec = Utility.diffDay(this.shiftTargetDate, this.keySet.stamp) !== 1;
  }

  async onSubmit() {
    this.submitting = true;
    await this.request();
    this.dialogRef.close(true);
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  request() {
    return new Promise<void>(async (resolve, reject) => {
      // 変更元の申請データからふりわけ変更した打刻の情報を削除する
      if (this.keySet.record.request !== undefined) {
        console.log('delete change source');
        const ejectFlags = this.keySet.record.request.ejectFlags;
        const actualStamps = this.keySet.record.request.actualStamps;
        const index = actualStamps.findIndex((timeStamp) => timeStamp === Utility.convertDateToDateMinite(this.keySet.stamp));
        // ふりわけ変更で1打刻減る分を詰める
        console.log(ejectFlags);
        let i: number;
        for (i = index; i < 8; i += 2) {
          ejectFlags[i] = ejectFlags[i + 2];
          actualStamps[i] = actualStamps[i + 2];
        }
        ejectFlags[i] = false; // 8 or 9
        actualStamps[i] = '';
        console.log(ejectFlags);

        const changeRequestCollection = collection(this.firestore, 'change-request');
        const requestQuery = query(
          changeRequestCollection,
          where('created', '==', this.keySet.record.request.created),
          where('uid', '==', this.user.uid)
        );
        const requestDoc = await getDocs(requestQuery);
        if (requestDoc.docs.length === 1 && requestDoc.docs[0] !== undefined) {
          const requestDocRef = doc(this.firestore, 'change-request', requestDoc.docs[0].id);
          await updateDoc(requestDocRef, {
            ejectFlags: ejectFlags,
            actualStamps: actualStamps
          });
        }
      }

      if (this.kind >= 2) {
        // 変更先の申請データにふりわけ変更した打刻を追加する
        const target = new Date(this.keySet.record.date);
        target.setDate(this.shiftTargetDate.getDate());
        const changeRequestCollection = collection(this.firestore, 'change-request');
        const changeDestRequestQuery = query(
          changeRequestCollection,
          where('uid', '==', this.user.uid),
          where('target', '==', Timestamp.fromDate(target))
        );
        const changeDestRequestDoc = await getDocs(changeDestRequestQuery);

        changeDestRequestDoc.docs.sort((a, b) => {
          return b.data()['created'].toDate().valueOf() - a.data()['created'].toDate().valueOf();
        });

        if (changeDestRequestDoc.docs[0] !== undefined) {
          // 変更先の日付に申請データがあるならば
          console.log('insert source');
          const ejectFlags = changeDestRequestDoc.docs[0].data()['ejectFlags'];
          const actualStamps = changeDestRequestDoc.docs[0].data()['actualStamps']
          const actualTimeStamps = changeDestRequestDoc.docs[0].data()['actualStamps'].map(val => Utility.createDateFromText(val));
          const insertIndex = actualTimeStamps.findIndex((stamp, index) => {
            if (index >= 2) {
              if (index % 2 === 0 && this.kind + 1 === Define.RequestKindDef.TemporaryLeave ||
                index % 2 === 1 && this.kind + 1 === Define.RequestKindDef.Reenter) {
              return this.keySet.stamp < stamp;
              }
            }
          });
          console.log('insertIndex=', insertIndex);
          if (insertIndex !== -1) {
            for (let i = 8 + (this.kind % 2); i > insertIndex; i -= 2) {
              console.log(i, 'to', i - 2);
              ejectFlags[i] = ejectFlags[i - 2];
              actualStamps[i] = actualStamps[i - 2];
            }
            ejectFlags[insertIndex] = false;
            actualStamps[insertIndex] = Utility.convertDateToDateMinite(this.keySet.stamp);
            console.log(ejectFlags);
            const changeDestRequestDocRef = doc(this.firestore, 'change-request', changeDestRequestDoc.docs[0].id);
            await updateDoc(changeDestRequestDocRef, {
              ejectFlags: ejectFlags,
              actualStamps: actualStamps
            });
          }
        }
      }

      if (!this.timeStampDoc) {
        throw new Error('target stamp document not found');
      }
      const docRef = doc(this.firestore, 'time-stamp', 'stamps', this.user.uid, this.timeStampDoc.id);
      await updateDoc(docRef, {target: Utility.convertDateToFullDateTime(this.shiftTargetDate)});
      // time-stampのフィールドに対して、追加・変更を行うが、
      // time-stampはIndexedDBを利用して管理しているため、firestoreの変更が即座に適用されない。
      // そのため、IndexedDBの変更もここで行う。

      // DB存在確認
      // DB読み込みに失敗している時点でふりわけ変更画面に遷移することもないため、必要ないはず..
      const checkDBExist = () => new Promise<boolean>(async (resolveParent, _rejectParent) => {
        const dqp = new Promise<IDBDatabase>(async (resolveChild, rejectChild) => {
          const req = window.indexedDB.open('workTimeDB');
          req.onsuccess = ev => resolveChild((ev.target as IDBOpenDBRequest).result);
          req.onerror = ev => rejectChild((ev.target as IDBOpenDBRequest).error);
        });
        const db = await dqp
        .then()
        .catch(error => {
          console.log(error);
          resolveParent(false);
        });
        if ((db as IDBDatabase).objectStoreNames === undefined) {
          resolveParent(false);
        }
        resolveParent((db as IDBDatabase).objectStoreNames.contains('time-stamp'));
      });

      const res = await checkDBExist();
      if (!res) {
        console.log('ObjectStore [time-stamp] does not exist. Redefinition ObjectStore');
        // キャッシュクリア等でDB・ストアが無くなっていた場合は新しく定義し直す
        const storeSchema: ObjectStoreMeta = {
          store: 'time-stamp',
          storeConfig: { keyPath: 'id', autoIncrement: false },
          storeSchema: [
            { name: 'uid', keypath: 'uid', options: { unique: false } },
            { name: 'doc', keypath: 'doc', options: { unique: false } }
          ]
        };
        this.dbService.createObjectStore(storeSchema);
      }
      const d = await firstValueFrom(this.dbService.getByKey('time-stamp', this.timeStampDoc.id));
      if (d === undefined) {
        console.error('not found ', this.timeStampDoc.data()['timeStamp'] , ' in indexedDB');
      } else {
        await firstValueFrom(
          this.dbService.update('time-stamp', {
            id: this.timeStampDoc.id,
            uid: this.user.uid,
            doc: {
              key: this.timeStampDoc.data()['key'],
              kind: this.timeStampDoc.data()['kind'],
              placeId: this.timeStampDoc.data()['placeId'],
              timeStamp: this.timeStampDoc.data()['timeStamp'],
              target: Utility.convertDateToFullDateTime(this.shiftTargetDate) // targetの追加
            }
          })
        );
      }
      resolve();
    });
  }
}
